import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  CrudOp,
  DataType,
  FilterOp,
  GlobalFilterOperator,
  IOWorkflow,
  IoWorkflowDetailWidgetData,
  SinkType,
  SourceType,
  TtmOp,
} from './io-workflow-builder.models';

type PanelName = 'info' | 'samay' | 'source' | 'ttm' | 'sink' | 'routing';

@Component({
  selector: 'app-io-workflow-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  templateUrl: './io-workflow-detail.component.html',
  styleUrl: './io-workflow-builder.component.scss',
})
export class IoWorkflowDetailComponent {
  @Input() set data(value: IoWorkflowDetailWidgetData | null | undefined) {
    if (!value?.workflow) return;
    const workflowChanged = this.currentWorkflowId !== value.workflow.id;
    this.workflow = value.workflow;
    this.onPersist = value.onPersist;
    this.onDeleteWorkflow = value.onDeleteWorkflow;
    this.onDuplicateWorkflow = value.onDuplicateWorkflow;
    if (workflowChanged) {
      this.currentWorkflowId = value.workflow.id;
      this.panels = this.createDefaultPanels();
      this.resetInlineBuffers();
    }
    this.cdr.markForCheck();
  }

  workflow!: IOWorkflow;

  panels = this.createDefaultPanels();
  newSMField = { columnName: '', dataType: 'string' as DataType };
  newTMMapping = { mapTo: '', attributes: '', dataType: 'string' as DataType };
  newStep = {
    operation: 'DIRECT_MAP' as TtmOp,
    inputAttrs: '',
    output: '',
    rangeStart: '',
    rangeEnd: '',
    defaultValue: '',
    separator: '',
  };
  newFiltRule = { operator: 'NOT_NULL' as FilterOp, attributes: '', value: '' };
  addStepFor: string | null = null;
  addFiltFor: string | null = null;
  newGFRow = { attribute: '', operator: 'EQUAL' as GlobalFilterOperator, value: '' };
  newDBParam = { key: '', value: '' };
  newAPIParam = { key: '', value: '' };
  newSvcEP = { version: '1.0', functionName: '' };
  newBizKey = '';

  readonly DATA_TYPES: DataType[] = [
    'string',
    'integer',
    'long',
    'double',
    'boolean',
    'datetime',
    'json',
    'array',
  ];
  readonly SOURCE_TYPES: SourceType[] = ['SFTP_SOURCE', 'DB_SOURCE', 'API_SOURCE'];
  readonly SINK_TYPES: SinkType[] = ['GRPC_SINK', 'API_SINK', 'CSV_SINK'];
  readonly GF_OPERATORS: GlobalFilterOperator[] = ['EQUAL', 'NOT_EQUAL', 'GREATER_THAN'];
  readonly CRUD_OPS: CrudOp[] = ['', 'insert', 'update', 'upsert'];
  readonly TTM_OPS: TtmOp[] = [
    'DIRECT_MAP',
    'CONCATENATE',
    'UPPERCASE',
    'LOWERCASE',
    'TRIM',
    'SUBSTRING',
    'VALIDATE_RANGE',
    'CONVERT_TO_JSON',
    'CONVERT_TO_ARRAY',
    'DATE_FORMAT',
    'REPLACE',
    'NULL_COALESCE',
    'DIFFERENCE',
    'SUM',
    'MULTIPLY',
  ];
  readonly FILTER_OPS: FilterOp[] = [
    'NOT_NULL',
    'NOT_EMPTY',
    'EQUAL',
    'NOT_EQUAL',
    'GREATER_THAN',
    'LESS_THAN',
  ];

  private readonly cdr = inject(ChangeDetectorRef);
  private onPersist: () => void = () => {};
  private onDeleteWorkflow: (workflowId: string) => void = () => {};
  private onDuplicateWorkflow: (workflowId: string) => void = () => {};
  private currentWorkflowId: string | null = null;

  togglePanel(name: PanelName): void {
    this.panels[name] = !this.panels[name];
  }

  updateField(field: keyof IOWorkflow, value: unknown): void {
    (this.workflow as unknown as Record<string, unknown>)[field] = value;
    this.persist();
  }

  addDBParam(): void {
    if (!this.newDBParam.key.trim()) return;
    this.workflow.db_parameters = [
      ...this.workflow.db_parameters,
      { id: this.createId('dbp'), key: this.newDBParam.key, value: this.newDBParam.value },
    ];
    this.newDBParam = { key: '', value: '' };
    this.persist();
  }

  removeDBParam(index: number): void {
    this.workflow.db_parameters.splice(index, 1);
    this.persist();
  }

  addAPIParam(): void {
    if (!this.newAPIParam.key.trim()) return;
    this.workflow.api_parameters = [
      ...this.workflow.api_parameters,
      { id: this.createId('api'), key: this.newAPIParam.key, value: this.newAPIParam.value },
    ];
    this.newAPIParam = { key: '', value: '' };
    this.persist();
  }

  removeAPIParam(index: number): void {
    this.workflow.api_parameters.splice(index, 1);
    this.persist();
  }

  addServiceEP(): void {
    if (!this.newSvcEP.functionName.trim()) return;
    this.workflow.serviceEndpoints = [
      ...this.workflow.serviceEndpoints,
      { id: this.createId('se'), version: this.newSvcEP.version, functionName: this.newSvcEP.functionName },
    ];
    this.newSvcEP = { version: '1.0', functionName: '' };
    this.persist();
  }

  removeSvcEP(index: number): void {
    this.workflow.serviceEndpoints.splice(index, 1);
    this.persist();
  }

  addSMField(): void {
    if (!this.newSMField.columnName.trim()) return;
    this.workflow.sourceMetadata = [
      ...this.workflow.sourceMetadata,
      {
        id: this.createId('sm'),
        columnName: this.newSMField.columnName.trim(),
        dataType: this.newSMField.dataType,
      },
    ];
    this.newSMField = { columnName: '', dataType: 'string' };
    this.persist();
  }

  removeSMField(index: number): void {
    this.workflow.sourceMetadata.splice(index, 1);
    this.persist();
  }

  addTMMapping(): void {
    if (!this.newTMMapping.mapTo.trim()) return;
    this.workflow.targetMetadata = [
      ...this.workflow.targetMetadata,
      {
        id: this.createId('tm'),
        mapTo: this.newTMMapping.mapTo.trim(),
        attributes: this.newTMMapping.attributes,
        dataType: this.newTMMapping.dataType,
        combineOperator: '',
        filtrationRules: [],
        steps: [],
        expanded: true,
      },
    ];
    this.newTMMapping = { mapTo: '', attributes: '', dataType: 'string' };
    this.persist();
  }

  removeTMMapping(index: number): void {
    this.workflow.targetMetadata.splice(index, 1);
    this.persist();
  }

  toggleTMExpand(mappingId: string): void {
    const mapping = this.workflow.targetMetadata.find((item) => item.id === mappingId);
    if (!mapping) return;
    mapping.expanded = !mapping.expanded;
  }

  openAddStep(mappingId: string): void {
    this.addStepFor = this.addStepFor === mappingId ? null : mappingId;
    this.newStep = {
      operation: 'DIRECT_MAP',
      inputAttrs: '',
      output: '',
      rangeStart: '',
      rangeEnd: '',
      defaultValue: '',
      separator: '',
    };
  }

  addTtmStep(mappingId: string): void {
    const mapping = this.workflow.targetMetadata.find((item) => item.id === mappingId);
    if (!mapping) return;
    mapping.steps = [
      ...mapping.steps,
      {
        id: this.createId('st'),
        step: String(mapping.steps.length + 1),
        ...this.newStep,
      },
    ];
    this.addStepFor = null;
    this.newStep = {
      operation: 'DIRECT_MAP',
      inputAttrs: '',
      output: '',
      rangeStart: '',
      rangeEnd: '',
      defaultValue: '',
      separator: '',
    };
    this.persist();
  }

  removeTtmStep(mappingId: string, index: number): void {
    const mapping = this.workflow.targetMetadata.find((item) => item.id === mappingId);
    if (!mapping) return;
    mapping.steps.splice(index, 1);
    mapping.steps.forEach((step, stepIndex) => {
      step.step = String(stepIndex + 1);
    });
    this.persist();
  }

  openAddFilt(mappingId: string): void {
    this.addFiltFor = this.addFiltFor === mappingId ? null : mappingId;
    this.newFiltRule = { operator: 'NOT_NULL', attributes: '', value: '' };
  }

  addFiltRule(mappingId: string): void {
    const mapping = this.workflow.targetMetadata.find((item) => item.id === mappingId);
    if (!mapping) return;
    mapping.filtrationRules = [
      ...mapping.filtrationRules,
      { id: this.createId('fr'), ...this.newFiltRule },
    ];
    this.addFiltFor = null;
    this.newFiltRule = { operator: 'NOT_NULL', attributes: '', value: '' };
    this.persist();
  }

  removeFiltRule(mappingId: string, index: number): void {
    const mapping = this.workflow.targetMetadata.find((item) => item.id === mappingId);
    if (!mapping) return;
    mapping.filtrationRules.splice(index, 1);
    this.persist();
  }

  addGFilter(): void {
    if (!this.newGFRow.attribute.trim()) return;
    this.workflow.globalFilters = [
      ...this.workflow.globalFilters,
      {
        id: this.createId('gf'),
        attribute: this.newGFRow.attribute.trim(),
        operator: this.newGFRow.operator,
        value: this.newGFRow.value,
      },
    ];
    this.newGFRow = { attribute: '', operator: 'EQUAL', value: '' };
    this.persist();
  }

  removeGFilter(index: number): void {
    this.workflow.globalFilters.splice(index, 1);
    this.persist();
  }

  addBizKey(): void {
    if (!this.newBizKey.trim()) return;
    this.workflow.businessKeys = [...this.workflow.businessKeys, this.newBizKey.trim()];
    this.newBizKey = '';
    this.persist();
  }

  removeBizKey(index: number): void {
    this.workflow.businessKeys.splice(index, 1);
    this.persist();
  }

  deleteWorkflow(): void {
    this.onDeleteWorkflow(this.workflow.id);
  }

  duplicateWorkflow(): void {
    this.onDuplicateWorkflow(this.workflow.id);
  }

  msToHuman(ms: string): string {
    const numeric = parseInt(ms, 10);
    if (Number.isNaN(numeric)) return '';
    if (numeric >= 3600000) return `${(numeric / 3600000).toFixed(1)}h`;
    if (numeric >= 60000) return `${Math.round(numeric / 60000)}m`;
    return `${Math.round(numeric / 1000)}s`;
  }

  maxRetries(): number {
    const pollTime = parseInt(this.workflow.pollTime, 10) || 0;
    const pollInterval = parseInt(this.workflow.pollInterval, 10) || 1;
    return pollInterval > 0 ? Math.floor(pollTime / pollInterval) : 0;
  }

  opLabel(op: TtmOp): string {
    const labels: Record<TtmOp, string> = {
      DIRECT_MAP: 'Direct Map',
      CONCATENATE: 'Concatenate',
      UPPERCASE: 'Uppercase',
      LOWERCASE: 'Lowercase',
      TRIM: 'Trim',
      SUBSTRING: 'Substring',
      VALIDATE_RANGE: 'Validate Range',
      CONVERT_TO_JSON: 'To JSON',
      CONVERT_TO_ARRAY: 'To Array',
      DATE_FORMAT: 'Date Format',
      REPLACE: 'Replace',
      NULL_COALESCE: 'Null Coalesce',
      DIFFERENCE: 'Difference',
      SUM: 'Sum',
      MULTIPLY: 'Multiply',
    };
    return labels[op];
  }

  opNeedsRange(op: TtmOp): boolean {
    return op === 'SUBSTRING' || op === 'VALIDATE_RANGE';
  }

  opNeedsSeparator(op: TtmOp): boolean {
    return op === 'CONCATENATE' || op === 'CONVERT_TO_ARRAY';
  }

  rangeLabel(op: TtmOp): [string, string] {
    return op === 'SUBSTRING' ? ['start', 'end'] : ['min', 'max'];
  }

  separatorLabel(op: TtmOp): string {
    return op === 'CONCATENATE' ? 'separator' : 'delimiter';
  }

  filtOpNeedsValue(op: FilterOp): boolean {
    return ['EQUAL', 'NOT_EQUAL', 'GREATER_THAN', 'LESS_THAN'].includes(op);
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  persist(): void {
    this.onPersist();
  }

  private createDefaultPanels(): Record<PanelName, boolean> {
    return {
      info: true,
      samay: false,
      source: true,
      ttm: true,
      sink: true,
      routing: false,
    };
  }

  private resetInlineBuffers(): void {
    this.newSMField = { columnName: '', dataType: 'string' };
    this.newTMMapping = { mapTo: '', attributes: '', dataType: 'string' };
    this.newStep = {
      operation: 'DIRECT_MAP',
      inputAttrs: '',
      output: '',
      rangeStart: '',
      rangeEnd: '',
      defaultValue: '',
      separator: '',
    };
    this.newFiltRule = { operator: 'NOT_NULL', attributes: '', value: '' };
    this.addStepFor = null;
    this.addFiltFor = null;
    this.newGFRow = { attribute: '', operator: 'EQUAL', value: '' };
    this.newDBParam = { key: '', value: '' };
    this.newAPIParam = { key: '', value: '' };
    this.newSvcEP = { version: '1.0', functionName: '' };
    this.newBizKey = '';
  }

  private createId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  }
}
